import { useMemo, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { useStreamWithBackend } from "@/features/chat/hooks/useStreamWithBackend";
import { useUser } from "@/providers/use-user";
import { useThreads } from "@/providers/use-threads";
import { useTranslation } from "react-i18next";
import {
  uiMessageReducer,
  isUIMessage,
  isRemoveUIMessage,
} from "@/types/ui";
import { normalizeMessages } from "@/utils/messages/messageNormalization";
import { useThreadIdPersistence } from "@/providers/StreamState/useThreadIdPersistence";
import { useThreadListSync } from "@/providers/StreamState/useThreadListSync";
import { useFirstTokenDetection } from "@/providers/StreamState/useFirstTokenDetection";
import { useBackendHealthCheck } from "@/hooks/useBackendHealthCheck";
import { useAgentStatus } from "@/hooks/useAgentStatus";
import { useRealtimeMessages } from "@/hooks/useRealtimeMessages";
import { type StreamContextType } from "./context";

/**
 * Orchestrate all streaming session logic.
 * Single responsibility: Coordinate all streaming-related hooks and return context value.
 *
 * This hook:
 * - Sets up the streaming connection with backend
 * - Manages thread ID persistence
 * - Syncs thread list when thread changes
 * - Detects first token for loader state
 * - Checks backend health
 *
 * @returns Complete StreamContext value
 */
export function useStreamSession(): StreamContextType {
  // Get userId and userName from User Context
  const { userId, userName } = useUser();
  // Get user's language preference
  const { i18n } = useTranslation();
  // Get thread management functions
  const { getThreads, setThreads } = useThreads();

  // Thread ID persistence with sessionStorage
  const { threadId, setThreadId: setThreadIdRaw } =
    useThreadIdPersistence();

  // Status change handler ref - will be set after useFirstTokenDetection runs
  const statusChangeHandlerRef = useRef<((status: string) => void) | null>(null);

  // Stable callback that calls the handler ref
  const onStatusChange = useCallback((status: string) => {
    if (statusChangeHandlerRef.current) {
      statusChangeHandlerRef.current(status);
    }
  }, []);

  // Core streaming hook (uses /api/ internally)
  const streamValue = useStreamWithBackend({
    userId,
    threadId: threadId ?? null,
    fetchStateHistory: true,
    onCustomEvent: (event: any, options: any) => {
      if (isUIMessage(event) || isRemoveUIMessage(event)) {
        options.mutate((prev: any) => {
          const ui = uiMessageReducer(prev.ui ?? [], event);
          return { ...prev, ui };
        });
      }
    },
    onThreadId: (id: string) => {
      setThreadIdRaw(id, { clearMessages: streamValue.clearMessages });
    },
    onClearThreadId: () => {
      setThreadIdRaw(null);
    },
    onStatusChange, // Pass status change handler
    threadMetadata: {
      participant_uid: userId,
      userName: userName || "User",
      language: i18n.language,
    },
  });

  // Normalize messages (deduplicate streaming tokens)
  const normalizedMessages = useMemo(
    () => normalizeMessages(streamValue.messages as any),
    [streamValue.messages],
  );

  // Wrap setThreadId to pass clearMessages callback
  const setThreadId = useMemo(
    () => (id: string | null) => {
      setThreadIdRaw(id, { clearMessages: streamValue.clearMessages });
    },
    [setThreadIdRaw, streamValue.clearMessages],
  );

  // First token detection for loader state (driven by status_change events)
  const { firstTokenReceived, currentStatus, resetFirstTokenReceived, wrappedSubmit, handleStatusChange } =
    useFirstTokenDetection();

  // Connect the status change handler to the ref SYNCHRONOUSLY before paint
  // useLayoutEffect runs synchronously after DOM mutations but before browser paint
  // This ensures the handler is available before any status events can arrive
  // Previously this was a direct assignment which could race with early events
  useLayoutEffect(() => {
    statusChangeHandlerRef.current = handleStatusChange;
  }, [handleStatusChange]);

  // Sync thread list when thread ID changes
  useThreadListSync(threadId, userId, getThreads, setThreads);

  // Check backend health on mount
  useBackendHealthCheck();

  // Subscribe to agent status (disabled during active SSE streaming)
  const { agentStatus, loading: agentStatusLoading, error: agentStatusError } =
    useAgentStatus(userId, threadId, streamValue.hasActiveStream);

  // Subscribe to real-time messages when agent is running BUT SSE stream is not active
  const { isListening: isRealtimeListening } = useRealtimeMessages(
    userId,
    threadId,
    agentStatus,
    streamValue.updateMessages,  // Pass the updater function from useStreamWithBackend
    streamValue.hasActiveStream  // Only listen when no active SSE stream (page reload scenario)
  );

  // Track if we've already attempted reconnection to avoid duplicate calls
  const hasAttemptedReconnectRef = useRef(false);

  // Destructure only the needed values to avoid unnecessary effect re-runs
  // Previously, using streamValue as a dependency caused re-runs on every render
  const { hasActiveStream, submit: streamSubmit } = streamValue;

  // Auto-reconnect to SSE stream when agent is running after page reload
  // This effect triggers when:
  // 1. Agent status is RUNNING_AGENT_LOOP (from Firestore via useAgentStatus)
  // 2. No active SSE stream (indicates page reload, not normal streaming)
  // 3. We haven't already attempted reconnection
  useEffect(() => {
    // Reset reconnection flag when thread changes
    if (!threadId) {
      hasAttemptedReconnectRef.current = false;
      return;
    }

    // Only attempt reconnection if:
    // - Agent is actively running
    // - No SSE stream is active (page reload scenario)
    // - We haven't attempted reconnection yet
    // - AgentStatus is not loading
    if (
      agentStatus === "RUNNING_AGENT_LOOP" &&
      !hasActiveStream &&
      !hasAttemptedReconnectRef.current &&
      !agentStatusLoading
    ) {
      hasAttemptedReconnectRef.current = true;
      console.log("🔄 Detected active agent loop after page reload - Reconnecting to SSE stream...");

      // Reconnect to the SSE stream (empty messages = just subscribe to existing broadcast)
      // The backend StreamRegistry will replay all buffered events
      streamSubmit({ messages: [] }, { forceNewThread: false });
    }
  }, [agentStatus, hasActiveStream, threadId, agentStatusLoading, streamSubmit]);

  // Tool approval handler for human-in-the-loop
  const sendToolApproval = useMemo(
    () =>
      async (params: {
        toolUseId: string;
        toolName: string;
        action: "approve" | "edit" | "reject";
        editedInput?: Record<string, any>;
      }) => {
        const { toolUseId, toolName, action, editedInput } = params;

        // Get original tool input from messages
        const toolInputMsg = normalizedMessages.find(
          (msg: any) =>
            msg.type === "tool_input" && msg.tool_use_id === toolUseId,
        );
        if (!toolInputMsg) {
          throw new Error(`Tool input not found for ${toolUseId}`);
        }

        const originalInput = (toolInputMsg as any).input_snapshot;

        // Build tool_results payload based on action
        // Backend (llm.py:166-209) handles this and converts to proper message structures
        // Backend also persists approval status to Firestore for state recovery after reload
        let toolResults: any[];

        if (action === "approve") {
          // APPROVE: Send tool_use structure (backend creates AssistantMessage)
          toolResults = [
            {
              tool_use_id: toolUseId,
              action: "approve",
              content: {
                id: toolUseId,
                name: toolName,
                input: originalInput,
              },
            },
          ];
        } else if (action === "reject") {
          // REJECT: Backend generates the rejection message
          toolResults = [
            {
              tool_use_id: toolUseId,
              action: "reject",
              content: "", // Backend will generate message
              is_error: true,
            },
          ];
        } else {
          // EDIT: Send edited tool_use (backend generates denial message)
          if (!editedInput) {
            throw new Error("editedInput is required for edit action");
          }

          toolResults = [
            {
              tool_use_id: toolUseId, // Original ID
              action: "edit",
              content: {
                id: `toolu_${Date.now()}`, // New ID for edited version
                name: toolName,
                input: editedInput,
              },
            },
          ];
        }

        // Optimistically update the tool_use content block approval_status
        // Two-role model: HITL state is stored in tool_use content blocks within AssistantMessage
        // This prevents approval buttons from reappearing on re-render
        // Map frontend action to backend approval_status values
        const approvalStatusMap: Record<string, string> = {
          approve: "approved",
          edit: "edited",
          reject: "rejected",
        };

        const newApprovalStatus = approvalStatusMap[action] || action;

        streamValue.updateMessages((prevMessages) =>
          prevMessages.map((msg) => {
            // Two-role model: Find AI message with tool_use content block
            if (msg.type === "ai" && Array.isArray(msg.content)) {
              const hasTargetToolUse = msg.content.some(
                (block: any) => block.type === "tool_use" && block.id === toolUseId
              );
              if (hasTargetToolUse) {
                // Update the tool_use block's approval_status
                return {
                  ...msg,
                  content: msg.content.map((block: any) => {
                    if (block.type === "tool_use" && block.id === toolUseId) {
                      return { ...block, approval_status: newApprovalStatus };
                    }
                    return block;
                  }),
                };
              }
            }
            return msg;
          }),
        );

        // Submit via tool_results (backend converts to proper Anthropic messages)
        streamValue.submit(
          {
            messages: [], // Empty - using tool_results instead
            tool_results: toolResults,
          },
          {
            streamMode: ["values"],
            streamSubgraphs: true,
            streamResumable: true,
          },
        );
      },
    [normalizedMessages, streamValue],
  );

  // Combine all values into context
  const contextValue = useMemo<StreamContextType>(
    () => ({
      ...streamValue,
      submit: wrappedSubmit(streamValue.submit), // Use wrapped submit
      messages: normalizedMessages as any,
      firstTokenReceived,
      currentStatus,
      resetFirstTokenReceived,
      threadId,
      setThreadId,
      sendToolApproval,
      createProject: streamValue.createProject,
      // Agent process visibility fields
      agentStatus,
      agentStatusLoading,
      agentStatusError,
      isRealtimeListening,
      hasActiveStream: streamValue.hasActiveStream,
    }),
    [
      streamValue,
      wrappedSubmit,
      normalizedMessages,
      firstTokenReceived,
      currentStatus,
      resetFirstTokenReceived,
      threadId,
      setThreadId,
      sendToolApproval,
      // Agent status deps
      agentStatus,
      agentStatusLoading,
      agentStatusError,
      isRealtimeListening,
    ],
  );

  return contextValue;
}
